import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMerchantScope } from '@noor/eats-api';
import { menuKeys, menuQueries } from '../api/menu-queries';
import { menuApi, type MenuFilters, type MenuScope } from '../api/menu-api';
import type { MenuCategory, MenuProduct } from './schema';

/**
 * Rows per request. 60 divides evenly into every grid width (2–6 columns), so
 * the last row is never ragged, and it is a fixed number on purpose: measuring
 * the DOM first delayed the initial request behind a mount + layout pass.
 */
export const PRODUCT_PAGE_SIZE = 60;

/** Cheap identity of a page, for spotting a gateway that ignores `offset`. */
function pageSignature(list: readonly MenuProduct[]): string {
  return `${list.length}:${String(list[0]?.id)}:${String(list[list.length - 1]?.id)}`;
}

export function useInfiniteMenuProducts(
  filters: MenuFilters,
  allBranches: boolean,
  pageSize = PRODUCT_PAGE_SIZE,
  enabled = true,
) {
  const { branchId, isReady } = useMerchantScope();
  const scope = !allBranches && branchId ? { restaurantId: branchId } : undefined;
  // Only the branch route can narrow by category; elsewhere it stays out of the
  // key and the request, so changing category does not refetch what it cannot
  // change.
  const categoryOnServer = Boolean(scope) && !filters.showcase;
  const { categoryId, ...rest } = filters;
  const requestFilters: MenuFilters = categoryOnServer ? filters : rest;
  return useInfiniteQuery({
    // Separate from the complete catalog, under the same mutation prefix.
    queryKey: [
      ...menuKeys.catalogs(),
      'infinite',
      menuKeys.catalog(scope?.restaurantId, requestFilters),
      categoryOnServer ? (categoryId ?? null) : null,
    ],
    enabled: isReady && enabled,
    initialPageParam: { offset: 0, limit: pageSize },
    queryFn: ({ signal, pageParam }) =>
      menuApi.getProductsPage(scope, requestFilters, pageParam, { signal }),
    getNextPageParam: (lastPage, pages, lastParam) => {
      const offset = lastParam.offset + lastPage.list.length;
      if (!lastPage.list.length || offset >= lastPage.meta.total) return undefined;
      // Stop when a gateway ignores offset instead of requesting forever.
      const signature = pageSignature(lastPage.list);
      for (let i = 0; i < pages.length - 1; i += 1) {
        if (pageSignature(pages[i]?.list ?? []) === signature) return undefined;
      }
      return { offset, limit: pageSize };
    },
  });
}

/**
 * The merchant/branch ids the Menu queries and mutations are scoped to.
 * Resolved from the session rather than passed down, so pages and features stay
 * free of id plumbing.
 */
export function useMenuScope(): MenuScope {
  const { branchId } = useMerchantScope();
  return { restaurantId: branchId ?? '' };
}

/**
 * The catalog for one branch, or for the whole merchant.
 *
 * `allBranches` is the toolbar's «Все филиалы» entry: it drops the scope, so
 * the request carries no `restaurant_id` and reads the merchant-wide route
 * instead (see `menuApi.get` for what that changes about prices and badges).
 * Otherwise the active branch scopes the read, and the selector makes that
 * choice visible rather than leaving it hidden in the store.
 *
 * The scope is also dropped while the branch list is still loading, so the
 * grid shows the merchant-wide catalog immediately instead of an empty state
 * that resolves into content.
 *
 * Filters are passed THROUGH to the request where the route accepts them —
 * see `menuApi.get` for which, and why they are also re-applied client-side.
 */
export function useMenu(
  filters?: MenuFilters,
  allBranches = false,
  options?: { enabled?: boolean },
) {
  const { branchId, isReady } = useMerchantScope();
  const scope = { restaurantId: branchId ?? '' };
  const scoped = !allBranches && scope.restaurantId !== '';
  // Do not let the unscoped ("all branches") fallback turn an unresolved
  // merchant/branch selection into a real catalog request. The backend treats
  // an empty scope as merchant-wide, so this gate is required for both modes.
  return useQuery(
    menuQueries.catalog(scoped ? scope : undefined, filters, {
      ...options,
      enabled: (options?.enabled ?? true) && isReady,
    }),
  );
}

/**
 * Merchant taxonomy without the product catalog.
 *
 * Published/showcase categories may be branch-scoped; normal categories are
 * merchant-wide. The API function keeps those semantics aligned with useMenu.
 */
export function useMenuCategories(
  filters?: Pick<MenuFilters, 'categoryStatus' | 'showcase'>,
  allBranches = false,
  options?: { enabled?: boolean },
) {
  const scope = useMenuScope();
  // Normal taxonomy is merchant-wide. Only the published/showcase endpoint
  // accepts a restaurant id, so branch changes must not fragment the normal
  // category cache into identical entries.
  const scoped = Boolean(filters?.showcase) && !allBranches && scope.restaurantId !== '';
  return useQuery(menuQueries.categories(scoped ? scope : undefined, filters, options));
}

/**
 * One product's authoritative record, for the edit drawer.
 *
 * The catalog list is a card projection — it omits the branch set, carries a
 * branch-resolved price, and holds one locale's text — so an editor seeded from
 * it would submit whatever the card happened to show. This re-reads the product
 * (once per language) when the drawer opens; it stays idle for a create.
 */
export function useMenuProduct(id: MenuProduct['id'] | undefined) {
  const scope = useMenuScope();
  return useQuery(menuQueries.product(scope, id));
}

/**
 * One category's translations, for the rename dialog.
 *
 * The catalog's category rows carry a single locale-resolved name, so the
 * dialog's Uz/En inputs would start blank without this — it re-reads the
 * category once per language. Idle when no id is given (the create flow).
 */
export function useMenuCategory(id: MenuCategory['id'] | undefined) {
  return useQuery(menuQueries.category(id));
}

/**
 * The merchant's whole catalog, for the branch editor's product picker.
 *
 * Not scoped to a branch (see `menuApi.listMerchantProducts`), so it takes no
 * scope and is safe to call while the branch being created has no id yet.
 */
export function useMerchantProducts(search = '') {
  return useQuery(menuQueries.merchantProducts(search));
}

/**
 * One branch's stocked products, with that branch's own prices — what seeds
 * step 2 of the branch editor on an edit. Idle until a branch id exists.
 */
export function useBranchProducts(restaurantId: string | undefined) {
  return useQuery(menuQueries.branchProducts(restaurantId));
}

/** The merchant's reusable modifier groups for the product modifier picker. */
export function useModifierGroups() {
  return useQuery(menuQueries.modifierGroups());
}
